import { Dispatch, SetStateAction, useRef, useState } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import React, { useEffect, useCallback } from "react";
import debounce from "lodash/debounce";

import { useSettingsContext } from "~/providers/SettingsContextProvider";
import { sortTerms } from "~/utils/terms";
import { FilterCategory } from "../forms/FilterCategory";
import { FilterFieldset, FilterLegend } from "../styled/FilterFieldset";
import PageMargins from "../styled/PageMargins";
import { Reveal } from "../ui/Reveal";
import { FilterDateRange } from "../forms/FilterDateRange";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import InputText from "~/components/styled/InputText";
import Button from "~/components/styled/Button";
import { SvgBackground } from "../ui/SvgBackground";
import { SkipToLink } from "../ui/SkipToLink";

const QuickSearch = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
  align-self: center;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        height: ${props.theme.spacePx(breakpoint, 7)};
        ${props.theme.textStyle(breakpoint, "h3Heading")};
      `;
    })}
`;
const SearchField = styled(InputText)<{ isError: boolean }>`
  width: 100%;
  height: 100%;
  border: none;
  flex-shrink: 0;
  color: ${({ isError }) => (isError ? "#f00" : "#000")};
  vertical-align: top;

  &::placeholder {
    color: #999 !important;
  }

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        ${props.theme.textStyle(breakpoint, `h3Heading`)};
        `;
    })}
`;

const SearchButton = styled(Button)<{ active: boolean }>`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: filter 0.3s, opacity 0.3s;

  & > span {
    transition: filter 0.3s;
  }
  &:active,
  &:focus-within {
    & > span {
      filter: invert(33.33%);
    }
  }

  @media (any-pointer: fine) {
    &:hover {
      & > span {
        filter: invert(33.33%);
      }
    }
  }

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        ${props.theme.textStyle(breakpoint, `h3Heading`)};
        width: ${props.theme.spacePx(breakpoint, 7)};
        height: ${props.theme.spacePx(breakpoint, 7)};
        `;
    })}
`;

const DATE_RANGE_BEGIN_MONTH = 0; // remember JS month are 0 indexed
const DATE_RANGE_BEGIN_YEAR = 1990;
const DATE_RANGE_ADDITIONAL_YEARS = 1;
const TYPES = ["exhibition", "event"];

export type FilterData = {
  programmeCategoryId: string[];
  resourceMediaTypeId: string[];
  resourceType: string[];
  type: string[];
  page: number;
  dateBegin: string;
  dateEnd: string;
  keyword: string;
};

export const defaultFilterData: FilterData = {
  type: TYPES,
  programmeCategoryId: [],
  resourceMediaTypeId: [],
  resourceType: [],
  page: 1,
  dateBegin: "",
  dateEnd: "",
  keyword: "",
};

export const FILTER_BASE_URL = "/archive/database";

const RESOURCE_TYPES = [
  {
    id: "3D",
    name: "3D Visit",
  },
  {
    id: "audio",
    name: "Audio",
  },
  {
    id: "documents",
    name: "Document",
  },
  ,
  {
    id: "images",
    name: "Image",
  },
  {
    id: "video",
    name: "Video",
  },
];

export const ArchiveFilter = ({
  id,
  filterBase = FILTER_BASE_URL,
  types = TYPES,
  isFilterOpen,
  initiallyOpen,
  filterData,
  setFilterData,
  filterQuery,
  setFilterQuery,
  firstYear,
}: {
  firstYear: number;
  id: string;
  types?: string[];
  filterBase?: string;
  filterData: FilterData;
  isFilterOpen: boolean;
  initiallyOpen: boolean;
  filterQuery: string;
  setFilterData: Dispatch<SetStateAction<FilterData>>;
  setFilterQuery: Dispatch<SetStateAction<string>>;
}) => {
  const settings = useSettingsContext();
  const router = useRouter();
  const config = useConfigContext();

  const inputRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const [isSearchFieldActive, setIsSearchFieldActive] = useState(false);

  const filterBaseUrl = `${config.baseUrl}${filterBase}`;

  const createQueryParams = (data: FilterData) => {
    const query = [];

    if (data?.programmeCategoryId?.length)
      query.push(`programmeCategoryId=${data.programmeCategoryId.join(",")}`);

    if (data?.resourceMediaTypeId?.length)
      query.push(`resourceMediaTypeId=${data.resourceMediaTypeId.join(",")}`);

    if (data?.resourceType?.length)
      query.push(`resourceType=${data.resourceType.join(",")}`);

    if (data?.dateBegin)
      query.push(`dateBegin=${data.dateBegin}&dateBeginCompare=gte`);

    if (data?.dateEnd) query.push(`dateEnd=${data.dateEnd}&dateEndCompare=lte`);

    if (data?.keyword?.trim().length > 2)
      query.push(`s=${data.keyword?.trim()}`);

    query.push(`type=${data.type.join(",")}`);
    query.push(`archived=1`);
    query.push("order=desc");
    query.push("orderby=meta.date_begin.date");
    query.push("perPage=24");
    query.push(`page=${data.page}`);

    return `?${query.join("&")}`;
  };

  const toggleResourceMediaType = useCallback(
    (termId: any) => {
      const data = { ...filterData };

      if (Array.isArray(data.resourceMediaTypeId)) {
        if (data.resourceMediaTypeId.includes(`${termId}`)) {
          data.resourceMediaTypeId = data.resourceMediaTypeId.filter(
            (id: any) => `${id}` !== `${termId}`
          );
        } else {
          data.resourceMediaTypeId.push(`${termId}`);
        }

        data.page = 1;

        router.push(`${filterBaseUrl}${createQueryParams(data)}`, undefined, {
          shallow: true,
        });
      }
    },
    [filterBaseUrl, filterData, router]
  );

  const toggleProgrammeCategory = useCallback(
    (termId: any) => {
      const data = { ...filterData };

      if (Array.isArray(data.programmeCategoryId)) {
        if (data.programmeCategoryId.includes(`${termId}`)) {
          data.programmeCategoryId = data.programmeCategoryId.filter(
            (id: any) => `${id}` !== `${termId}`
          );
        } else {
          data.programmeCategoryId.push(`${termId}`);
        }

        data.page = 1;

        router.push(`${filterBaseUrl}${createQueryParams(data)}`, undefined, {
          shallow: true,
        });
      }
    },
    [filterBaseUrl, filterData, router]
  );

  const toggleResourceType = useCallback(
    (termId: any) => {
      const data = { ...filterData };

      if (Array.isArray(data.resourceType)) {
        if (data.resourceType.includes(`${termId}`)) {
          data.resourceType = data.resourceType.filter(
            (id: any) => `${id}` !== `${termId}`
          );
        } else {
          data.resourceType.push(`${termId}`);
        }

        data.page = 1;

        router.push(`${filterBaseUrl}${createQueryParams(data)}`, undefined, {
          shallow: true,
        });
      }
    },
    [filterBaseUrl, filterData, router]
  );

  const updateKeyword = useCallback(
    (keyword: string) => {
      const data = { ...filterData };

      const trimmedKeyword = keyword.trim();

      if (trimmedKeyword !== data.keyword) {
        data.keyword = trimmedKeyword;
        data.page = 1;
        router.push(`${filterBaseUrl}${createQueryParams(data)}`, undefined, {
          shallow: true,
        });
      }
    },
    [filterBaseUrl, filterData, router]
  );

  const updateKeywordDebounced = debounce(updateKeyword, 400);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const setQueryParams = () => {
      const params = new URLSearchParams(document.location.search);

      const data: FilterData = { ...defaultFilterData };

      const programmeCategoryId = params.get("programmeCategoryId") ?? "";
      if (programmeCategoryId) {
        data.programmeCategoryId = programmeCategoryId.split(",") ?? [];
      } else {
        data.programmeCategoryId = [];
      }

      const resourceMediaTypeId = params.get("resourceMediaTypeId") ?? "";
      if (resourceMediaTypeId) {
        data.resourceMediaTypeId = resourceMediaTypeId.split(",") ?? [];
      } else {
        data.resourceMediaTypeId = [];
      }

      const resourceType = params.get("resourceType") ?? "";
      if (resourceType) {
        data.resourceType = resourceType.split(",") ?? [];
      } else {
        data.resourceType = [];
      }

      const type = params.get("type") ?? "";
      if (type) {
        data.type = type.split(",");
      } else {
        data.type = types;
      }

      data.keyword = params.get("s") ?? "";

      const page = params.get("page") ?? "1";
      if (page) data.page = parseInt(page);

      if (params.get("dateBegin"))
        data.dateBegin = params.get("dateBegin") ?? "";

      if (params.get("dateEnd")) data.dateEnd = params.get("dateEnd") ?? "";

      setFilterData(data);
      setFilterQuery(createQueryParams(data));
    };

    if (filterQuery === "") setQueryParams();

    router.events.on("routeChangeComplete", setQueryParams);

    return () => {
      router.events.off("routeChangeComplete", setQueryParams);
    };
  }, [filterQuery, router.events, setFilterQuery, setFilterData, types]);

  return (
    <PageMargins>
      <form
        action={FILTER_BASE_URL}
        method="GET"
        onSubmit={(event) => {
          event.preventDefault();
          updateKeywordDebounced(inputRef?.current?.value?.trim());
        }}
      >
        <Reveal
          id={id}
          open={isFilterOpen}
          role="form"
          initiallyOpen={initiallyOpen}
        >
          <SkipToLink id="listing">skip filters</SkipToLink>
          {settings?.taxonomies?.programmeCategory?.length > 0 && (
            <FilterFieldset addPaddingTop>
              <FilterLegend>Programme TYPE</FilterLegend>
              {["exhibitions", "event", "events", "off-site", "international"]
                .reduce((acc: any[], term: any) => {
                  const cat = settings?.taxonomies?.programmeCategory.find(
                    (cat: any) => cat.slug === term
                  );

                  if (cat) acc.push(cat);
                  return acc;
                }, [] as any[])
                .map((term: any, index: number) => {
                  return (
                    <FilterCategory
                      defaultChecked={
                        Array.isArray(filterData.programmeCategoryId) &&
                        filterData.programmeCategoryId.includes(`${term.id}`)
                      }
                      name="programmeCategoryId"
                      key={`filter-cat-${term.id}`}
                      id={`filter-cat-${term.id}`}
                      value={term.id}
                      label={term.name}
                      onClick={() => {
                        toggleProgrammeCategory(term.id);
                      }}
                    />
                  );
                })}
            </FilterFieldset>
          )}

          {settings?.taxonomies?.resourceMediaType?.length > 0 && (
            <FilterFieldset addPaddingTop>
              <FilterLegend>MEDIA TYPE</FilterLegend>
              {sortTerms(settings?.taxonomies?.resourceMediaType).map(
                (term: any, index: number) => {
                  return (
                    <FilterCategory
                      defaultChecked={
                        Array.isArray(filterData.resourceMediaTypeId) &&
                        filterData.resourceMediaTypeId.includes(`${term.id}`)
                      }
                      name="resourceMediaTypeId"
                      key={`filter-rmt-${term.id}`}
                      id={`filter-rmt-${term.id}`}
                      value={term.id}
                      label={term.name}
                      onClick={() => {
                        toggleResourceMediaType(term.id);
                      }}
                    />
                  );
                }
              )}
            </FilterFieldset>
          )}
          <FilterFieldset addPaddingTop>
            <FilterLegend>RESOURCE TYPE</FilterLegend>
            {RESOURCE_TYPES.map((term: any, index: number) => {
              return (
                <FilterCategory
                  defaultChecked={filterData.resourceType.includes(
                    `${term.id}`
                  )}
                  name="resourceType"
                  key={`filter-cat-${term.id}`}
                  id={`filter-cat-${term.id}`}
                  value={term.id}
                  label={term.name}
                  onClick={() => {
                    toggleResourceType(term.id);
                  }}
                />
              );
            })}
          </FilterFieldset>

          <FilterFieldset addPaddingTop>
            <FilterLegend>DATES BETWEEN</FilterLegend>
            <FilterDateRange
              firstMonth={DATE_RANGE_BEGIN_MONTH}
              firstYear={DATE_RANGE_BEGIN_YEAR}
              addUntilYears={DATE_RANGE_ADDITIONAL_YEARS}
              from={filterData.dateBegin}
              until={filterData.dateEnd}
              onRangeChange={(from, until) => {
                const data = { ...filterData };
                if (from?.getTime() && until?.getTime()) {
                  data.dateBegin = from.toISOString()?.split("T")?.[0] ?? "";
                  data.dateEnd =
                    new Date(until.getFullYear(), until.getMonth() + 1, 0)
                      .toISOString()
                      ?.split("T")?.[0] ?? "";
                } else {
                  data.dateBegin = "";
                  data.dateEnd = "";
                }

                data.page = 1;

                router.push(
                  `${filterBaseUrl}${createQueryParams(data)}`,
                  undefined,
                  {
                    shallow: true,
                  }
                );
              }}
            />
          </FilterFieldset>
        </Reveal>
        <FilterFieldset addPaddingTop grid>
          <FilterLegend>SEARCH FOR</FilterLegend>
          <QuickSearch>
            <SearchField
              placeholder="KEYWORD"
              isError={false}
              ref={inputRef}
              defaultValue={filterData.keyword}
              onChange={() => {
                if (inputRef?.current?.value?.trim()?.length > 2) {
                  setIsSearchFieldActive(true);
                } else {
                  setIsSearchFieldActive(false);
                }
                updateKeywordDebounced(inputRef?.current?.value?.trim());
              }}
            />
            <SearchButton aria-label="search" active={isSearchFieldActive}>
              <SvgBackground
                type="arrow"
                position="center center"
                width="100%"
                height="100%"
              />
            </SearchButton>
          </QuickSearch>
        </FilterFieldset>
      </form>
    </PageMargins>
  );
};
